import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { EventDataTagTypeEnum } from 'src/app/core/services/event-data/event-data.service';
import { CoAppTypeEnum, ILoanApplication } from 'src/app/core/services/loan-application/loan-application.model';
import { LoanApplicationService } from 'src/app/core/services/loan-application/loan-application.service';
import { EventLogApiService, MobileApiService } from 'src/app/core/services/mobile-api';
import { RoutingPathsEnum, RoutingService } from 'src/app/core/services/routing/routing.service';
import { TagDataService } from 'src/app/core/services/tag-data/tag-data.service';
import { OrganizationUtils } from 'src/app/core/utils/organization-utils';
import { QuestionTypeEnum } from 'src/app/landing/co-app/co-app-common-questions/co-app-common-questions.component';

@Component({
	selector: 'op-status-co-app-offered-v1',
	templateUrl: './co-app-offered-v1.component.html',
	styleUrls: ['./co-app-offered-v1.component.scss']
})
export class CoAppOfferedV1Component implements OnInit, OnDestroy {
	private buffer = 0;
	private destroy$ = new Subject<void>();
	needStrongCredit: boolean;
	isFooterVisible = false;
	loanApp: ILoanApplication;
	enableCoAppInvite: boolean;
	questionTypeEnum = QuestionTypeEnum;

	constructor(
		private eventLogService: EventLogApiService,
		private tagDataService: TagDataService,
		protected loanAppService: LoanApplicationService,
		private dialogService: DialogService,
		protected translocoService: TranslocoService,
		private routingService: RoutingService,
		private mobileService: MobileApiService
	) {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	newApplicationStartDate: string;
	issuingOrganizationIsOportun: boolean;

	ngOnInit(): void {
		this.mobileService.getCoAppInviteScreenEligibility(this.loanAppService.loanApplicationId).subscribe({
			next: (resp) => {
				this.enableCoAppInvite = resp.eligible;
			}
		});
		this.translocoService.langChanges$.pipe(takeUntil(this.destroy$)).subscribe((lang) => {
			const today = new Date();
			const futureDate = new Date(today.setDate(today.getDate() + 30));
			this.newApplicationStartDate = futureDate.toLocaleDateString(lang, { month: 'long', day: 'numeric' });
		});

		this.loanApp = this.loanAppService.getLoanApp();
		this.issuingOrganizationIsOportun = OrganizationUtils.isOportun(this.loanApp.issuingOrganization);
		this.needStrongCredit = this.loanApp.coAppType === CoAppTypeEnum.CECR;

		this.tagDataService.link(
			{},
			{
				event_category: 'co_app_offered_v1',
				tealium_event: `Co New Offer Screen Init`,
				coAppType: this.loanApp.coAppType
			}
		);
	}

	@HostListener('window:scroll', [])
	onScroll() {
		const footerEl = document.querySelector('op-footer');
		if (!footerEl) return;
		const rect = footerEl.getBoundingClientRect();
		const windowHeight = window.innerHeight;
		const top = rect.top - this.buffer;
		this.isFooterVisible = top <= windowHeight;
		this.buffer = this.isFooterVisible ? 200 : 0;
	}

	callUs(): void {
		const tagEvent = {
			event_category: 'co_app_offered_v1',
			event_action: 'Button Click',
			event_label: 'Call Now',
			event_type: EventDataTagTypeEnum.coAppOfferScreenOnlineCallNow
		};
		this.tagDataService.link({ application_status: this.loanApp.applicationStatus }, tagEvent);
		this.dialogService
			.openContactUsDialogWithPhone(this.translocoService.translate('CALL_US.coAppHelpLine'))
			.subscribe();
		const event = {
			applicantId: this.loanAppService.getCurrentApplicant()?.id,
			eventType: EventDataTagTypeEnum.coAppOfferScreenOnlineCallNow,
			loanApplicationId: this.loanApp.id
		};
		this.eventLogService.logLoanAppEvent(event).subscribe();
	}

	inviteCoApplicant(): void {
		const tagEvent = {
			event_category: 'co_app_offered_v1',
			event_action: 'Button Click',
			event_label: 'Invite Co-Applicant',
			event_type: EventDataTagTypeEnum.coAppOfferScreenOnlineInvite
		};
		this.tagDataService.link({ application_status: this.loanApp.applicationStatus }, tagEvent);
		this.routingService.route(RoutingPathsEnum.coAppInvite);
	}

	doItLater(): void {
		const tagEvent = {
			event_category: 'co_app_offered_v1',
			event_action: 'Button Click',
			event_label: 'Do it later',
			event_type: EventDataTagTypeEnum.coAppOfferScreenOnlineDoItLater
		};
		this.tagDataService.link({}, tagEvent);
		const event = {
			applicantId: this.loanAppService.getCurrentApplicant()?.id,
			eventType: EventDataTagTypeEnum.coAppOfferScreenOnlineDoItLater,
			loanApplicationId: this.loanApp.id
		};
		this.eventLogService.logLoanAppEvent(event).subscribe();

		const message = `<p>${this.translocoService.translate('CO_APP.OFFER.ADD_COAPPLICANT.undecidedMessage')}</p>`;
		const data = {
			title: this.translocoService.translate('CO_APP.OFFER.ADD_COAPPLICANT.header'),
			message,
			confirmText: this.translocoService.translate('GLOBAL.ok')
		};
		this.dialogService.openSimpleMessageDialog(data);
	}
}
